import React, { useEffect, useState, lazy, createElement } from 'react';
import { CheckCircle2, XCircle, Clock, Star, FileText, Zap, ShieldCheck, Users, BookOpen, Gift, ChevronDown, AlertCircle, ArrowRight, MessageSquare, Calendar, Layers, Check, Moon, Sparkles, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// --- Sub-components ---
const CountdownTimer = ({
  dark = false
}: {
  dark?: boolean;
}) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 27,
    seconds: 35
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const bgClass = dark ? 'bg-[#1A0B2E]' : 'bg-[#1A0B2E]';
  return <div className="flex gap-2 font-mono text-xl md:text-4xl font-bold text-white items-start justify-center" data-id="element-1">
      <div className={`${bgClass} px-3 py-2 rounded border border-white/10 shadow-lg`} data-id="element-2">
        {String(timeLeft.hours).padStart(2, '0')}h
      </div>
      <span className="text-white self-center" data-id="element-3">:</span>
      <div className={`${bgClass} px-3 py-2 rounded border border-white/10 shadow-lg`} data-id="element-4">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </div>
      <span className="text-white self-center" data-id="element-5">:</span>
      <div className={`${bgClass} px-3 py-2 rounded border border-white/10 shadow-lg`} data-id="element-6">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </div>
    </div>;
};
const FloatingNotification = () => {
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const notifications = [{
    n: 'Sofía M.',
    l: 'Buenos Aires, Argentina',
    t: 'hace 6 minutos',
    p: 'el pack completo'
  }, {
    n: 'Ricardo P.',
    l: 'Ciudad de México, México',
    t: 'hace 2 minutos',
    p: 'el pack completo'
  }, {
    n: 'Andrea L.',
    l: 'Bogotá, Colombia',
    t: 'hace 15 minutos',
    p: 'el pack completo'
  }, {
    n: 'Martín G.',
    l: 'Santiago, Chile',
    t: 'hace 8 minutos',
    p: 'el pack completo'
  }, {
    n: 'Valentina R.',
    l: 'Lima, Perú',
    t: 'hace 4 minutos',
    p: 'el pack completo'
  }, {
    n: 'Diego S.',
    l: 'Montevideo, Uruguay',
    t: 'hace 11 minutos',
    p: 'el pack completo'
  }, {
    n: 'Camila T.',
    l: 'Guayaquil, Ecuador',
    t: 'hace 3 minutos',
    p: 'el pack completo'
  }, {
    n: 'Fernando A.',
    l: 'Asunción, Paraguay',
    t: 'hace 9 minutos',
    p: 'el pack completo'
  }, {
    n: 'Lucía M.',
    l: 'Guadalajara, México',
    t: 'hace 7 minutos',
    p: 'el pack completo'
  }, {
    n: 'Joaquín B.',
    l: 'Córdoba, Argentina',
    t: 'hace 13 minutos',
    p: 'el pack completo'
  }, {
    n: 'Paula C.',
    l: 'Medellín, Colombia',
    t: 'hace 5 minutos',
    p: 'el pack completo'
  }, {
    n: 'Sebastián V.',
    l: 'San José, Costa Rica',
    t: 'hace 10 minutos',
    p: 'el pack completo'
  }, {
    n: 'Daniela F.',
    l: 'La Paz, Bolivia',
    t: 'hace 12 minutos',
    p: 'el pack completo'
  }, {
    n: 'Tomás G.',
    l: 'Ciudad de Panamá, Panamá',
    t: 'hace 1 minuto',
    p: 'el pack completo'
  }, {
    n: 'Antonia P.',
    l: 'Valparaíso, Chile',
    t: 'hace 14 minutos',
    p: 'el pack completo'
  }];
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setHasScrolled(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (!hasScrolled) return;
    let hideTimeout: NodeJS.Timeout;
    const showNotification = () => {
      setVisible(true);
      hideTimeout = setTimeout(() => {
        setVisible(false);
      }, 4000);
    };
    const initialTimeout = setTimeout(showNotification, 3000);
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % notifications.length);
      showNotification();
    }, 15000);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(hideTimeout);
      clearInterval(interval);
    };
  }, [hasScrolled]);
  const current = notifications[currentIndex];
  return <AnimatePresence data-id="element-7">
      {visible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} className="fixed bottom-6 left-6 z-50 bg-[#1A0B2E] shadow-2xl rounded-lg p-4 border border-[#C9A84C]/30 flex items-center gap-4 max-w-sm" data-id="element-8">
          <div className="w-10 h-10 bg-[#8900F2]/20 rounded-full flex items-center justify-center text-[#8900F2]" data-id="element-9">
            <Sparkles size={20} data-id="element-10" />
          </div>
          <div data-id="element-11">
            <p className="text-[10px] font-semibold text-[#C9A84C] uppercase tracking-widest" data-id="element-12">
              Transformación iniciada
            </p>
            <p className="text-sm text-[#F5F0E8]" data-id="element-13">
              <span className="font-bold" data-id="element-14">{current.n}</span> de {current.l}
              <br data-id="element-15" />
              adquirió{' '}
              <span className="font-bold text-[#C9A84C]" data-id="element-16">{current.p}</span>
            </p>
            <p className="text-xs text-white/40 mt-1" data-id="element-17">{current.t}</p>
          </div>
          <button onClick={() => setVisible(false)} className="text-white/20 hover:text-white/50" data-id="element-18">
            <XCircle size={16} data-id="element-19" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
const FloatingWhatsApp = () => {
  return <motion.a href="https://wa.me/5492966244076?text=Hola!%20Vengo%20de%20la%20p%C3%A1gina%20METODO%20DE%20LOS%2033%20RITOS%20y%20necesito%20ayuda%20con%20mi%20pago.%20%C2%BFMe%20podr%C3%ADas%20asesorar%3F" target="_blank" rel="noopener noreferrer" aria-label="Soporte por WhatsApp" initial={{
    scale: 0,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} transition={{
    delay: 1.5,
    type: 'spring',
    stiffness: 200
  }} whileHover={{
    scale: 1.1
  }} whileTap={{
    scale: 0.95
  }} className="fixed bottom-6 right-6 z-[60] w-14 h-14 md:w-16 md:h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-[0_8px_30px_rgba(37,211,102,0.5)] hover:bg-[#20ba5a] transition-colors" data-id="element-20">
      <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" data-id="element-21"></span>
      <svg viewBox="0 0 32 32" className="w-7 h-7 md:w-8 md:h-8 text-white relative z-10" fill="currentColor" data-id="element-22">
        <path d="M16 2C8.28 2 2 8.28 2 16c0 2.75.81 5.31 2.2 7.47L2 30l6.74-2.16A13.93 13.93 0 0 0 16 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.5c-2.32 0-4.5-.66-6.34-1.8l-.45-.27-4 1.28 1.3-3.9-.3-.46A11.5 11.5 0 1 1 27.5 16c0 6.34-5.16 11.5-11.5 11.5zm6.55-8.6c-.36-.18-2.12-1.05-2.45-1.17-.33-.12-.57-.18-.81.18-.24.36-.93 1.17-1.14 1.41-.21.24-.42.27-.78.09-.36-.18-1.51-.56-2.88-1.78-1.06-.95-1.78-2.12-1.99-2.48-.21-.36-.02-.55.16-.73.16-.16.36-.42.54-.63.18-.21.24-.36.36-.6.12-.24.06-.45-.03-.63-.09-.18-.81-1.95-1.11-2.67-.29-.7-.59-.6-.81-.61-.21-.01-.45-.01-.69-.01s-.63.09-.96.45-1.26 1.23-1.26 3 1.29 3.48 1.47 3.72c.18.24 2.54 3.87 6.15 5.42.86.37 1.53.59 2.05.76.86.27 1.65.23 2.27.14.69-.1 2.12-.87 2.42-1.7.3-.84.3-1.55.21-1.7-.09-.15-.33-.24-.69-.42z" data-id="element-23" />
      </svg>
    </motion.a>;
};
const PaymentTrustBlock = ({
  introText
}: {
  introText?: string;
}) => <div className="flex flex-col items-center gap-3 mb-4 md:mb-6 text-center" data-id="element-24">
    <div className="max-w-md mx-auto text-left" data-id="element-25">
      <p className="text-[#C9A84C] font-bold text-xs md:text-sm uppercase tracking-widest mb-1.5 text-center" data-id="element-26">
        Pago seguro y conversión a tu moneda local
      </p>
      <p className="text-white/70 text-[11px] md:text-sm font-light leading-[1.5]" data-id="element-27">
        {introText ?? 'Al presionar el botón de compra, serás redirigido a nuestro checkout seguro. El precio base del producto es de USD 10. Dentro del checkout podrás visualizar el importe convertido a la moneda disponible para tu país antes de confirmar la compra. Ediciones Umbral no agrega impuestos ni recargos propios por realizar la conversión de USD 10 a tu moneda local. El importe total será informado claramente en el checkout antes de completar el pago. Cualquier cargo externo aplicado por el banco, la tarjeta o las regulaciones de cada país es independiente de Ediciones Umbral.'}
      </p>
    </div>
    <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3" data-id="element-28">
      <div className="bg-white px-3 py-1.5 rounded-md flex items-center justify-center shadow-sm" data-id="element-29">
        <span className="text-[#00B1EA] font-bold text-xs md:text-sm tracking-tight" data-id="element-30">
          mercado pago
        </span>
      </div>
      <div className="bg-[#1A1F71] px-3 py-1.5 rounded-md flex items-center justify-center shadow-sm" data-id="element-31">
        <span className="text-white font-bold italic text-xs md:text-sm tracking-wider" data-id="element-32">
          VISA
        </span>
      </div>
      <div className="bg-white px-3 py-1.5 rounded-md flex items-center gap-0.5 shadow-sm" data-id="element-33">
        <span className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#EB001B]" data-id="element-34"></span>
        <span className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#F79E1B] -ml-1.5" data-id="element-35"></span>
      </div>
    </div>
    <div className="bg-[#F0FDF4] border border-[#16A34A]/30 rounded-xl px-4 py-2.5 md:py-3 max-w-md mx-auto" data-id="element-36">
      <p className="text-[#15803D] font-medium leading-snug" style={{
      fontSize: '13px'
    }} data-id="element-37">
        📦 <span className="font-bold" data-id="element-38">ENTREGA DIGITAL:</span> El producto es
        100% digital. No tiene costos de envío y recibirás el acceso en el
        correo ingresado durante el pago.
      </p>
    </div>
  </div>;
const TrustBadgesRow = () => <div className="flex flex-wrap justify-center gap-3 md:gap-6 mt-4 md:mt-5" data-id="element-39">
    <div className="flex items-center gap-1.5 text-white/70 text-[10px] md:text-xs font-medium" data-id="element-40">
      <ShieldCheck size={14} className="text-[#16A34A]" data-id="element-41" />
      <span data-id="element-42">Compra Protegida por Mercado Pago</span>
    </div>
    <div className="flex items-center gap-1.5 text-white/70 text-[10px] md:text-xs font-medium" data-id="element-43">
      <Zap size={14} className="text-[#C9A84C]" data-id="element-44" />
      <span data-id="element-45">Acceso Inmediato (PDF)</span>
    </div>
    <div className="flex items-center gap-1.5 text-white/70 text-[10px] md:text-xs font-medium" data-id="element-46">
      <Award size={14} className="text-[#C9A84C]" data-id="element-47" />
      <span data-id="element-48">7 Días de Garantía</span>
    </div>
  </div>;
const SectionTitle = ({
  children,
  subtitle,
  light = false,
  gold = false
}: {
  children: React.ReactNode;
  subtitle?: string;
  light?: boolean;
  gold?: boolean;
}) => <div className="text-center mb-3 md:mb-16" data-id="element-49">
    <h2 className={`text-3xl md:text-6xl font-bold mb-1.5 md:mb-6 ${gold ? 'text-[#C9A84C]' : 'text-white'}`} data-id="element-50">
      {children}
    </h2>
    {subtitle && <p className={`text-base md:text-xl font-light leading-[1.5] md:leading-relaxed max-w-3xl mx-auto px-4 ${light ? 'text-white/80' : 'text-white/60'}`} data-id="element-51">
        {subtitle}
      </p>}
  </div>;
const CTAButton = ({
  children,
  className = '',
  href = 'https://el-metodo-de-los-33-ritos.impultienda.ar/checkout',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Método 33 Ritos',
        value: 10,
        currency: 'USD'
      });
    }
    if (onClick) onClick();
  };
  const content = <motion.button animate={{
    scale: [1, 1.03, 1]
  }} transition={{
    duration: 2.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className={`${`
        w-full md:w-auto px-6 py-4 md:px-12 md:py-6 rounded-full font-bold text-xl transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(22,163,74,0.5)]
        bg-[#16A34A] text-white hover:bg-[#15803D]
        ${className}
      `} text-[14px] pt-[10px] pb-[10px]`} onClick={handleClick} data-id="element-52">
      {children}
    </motion.button>;
  if (href) {
    const isInternal = href.startsWith('#');
    return <a href={href} target={isInternal ? undefined : '_blank'} rel={isInternal ? undefined : 'noopener noreferrer'} className="w-full md:w-auto block" onClick={e => {
      handleClick(e);
      if (isInternal) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-53">
        {content}
      </a>;
  }
  return content;
};
const CTAMicroCopy = () => <p className="text-white/50 text-[10px] md:text-xs text-center mt-2 italic leading-snug" data-id="element-54">
    🔒 Serás redirigido a un checkout seguro para revisar el importe en tu
    moneda antes de confirmar el pago.
  </p>;
// --- Main Page ---
export function LandingPage() {
  useEffect(() => {
    if (!window.fbq) {
      const f = window as any;
      const b = document;
      const e = 'script';
      const v = 'https://connect.facebook.net/en_US/fbevents.js';
      let n: any, t: any, s: any;
      f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      n = f.fbq;
      if (!f._fbq) f._fbq = n;
      f.fbq.push = f.fbq;
      f.fbq.loaded = !0;
      f.fbq.version = '2.0';
      f.fbq.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) s.parentNode.insertBefore(t, s);else b.head.appendChild(t);
    }
    if (window.fbq) {
      window.fbq('init', '1297023029023257');
      window.fbq('track', 'PageView');
    }
  }, []);
  return <div className="min-h-screen bg-[#1A0B2E] text-[#F5F0E8] selection:bg-[#8900F2] selection:text-white" data-id="element-55">
      <FloatingNotification data-id="element-56" />

      {/* Hero Section */}
      <header className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden bg-gradient-to-b from-[#1A0B2E] to-[#2D00F7]/20" data-id="element-57">
        <div className="absolute inset-0 z-0 opacity-30" data-id="element-58">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#8900F2] blur-[150px] rounded-full" data-id="element-59"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#2D00F7] blur-[150px] rounded-full" data-id="element-60"></div>
        </div>

        <div className="container mx-auto px-4 py-4 md:py-16 relative z-10" data-id="element-61">
          <div className="max-w-5xl mx-auto text-center" data-id="element-62">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 bg-white/5 border border-[#C9A84C]/30 px-4 py-2 rounded-full text-[#C9A84C] text-xs md:text-sm font-bold mb-2 md:mb-8 backdrop-blur-md uppercase tracking-[0.2em]" data-id="element-63">
              🌑 EL MÉTODO DE LOS 33 RITOS™
            </motion.div>

            <h1 className="md:text-8xl font-bold mb-2 md:mb-8 text-white text-[20px] leading-snug md:leading-tight" data-id="element-64">
              Un material completo de{' '}
              <span className="text-[#C9A84C]" data-id="element-65">Psicología Simbólica</span> para
              cerrar ciclos, soltar vínculos y renacer desde adentro.
            </h1>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            duration: 1
          }} className="relative max-w-4xl mx-auto mb-5 md:mb-16" data-id="element-66">
              <div className="absolute inset-0 bg-[#8900F2] blur-[100px] opacity-20" data-id="element-67"></div>
              <img src="https://cdn.magicpatterns.com/uploads/nHMCVwm4Fede3rMn2MjRDL/metodo_33_ritos_landing_hero_1024.webp" alt="El Método de los 33 Ritos Mockup" width="1024" height="1024" loading="eager" fetchPriority="high" decoding="async" className="relative z-10 w-[95%] mx-auto h-auto object-contain rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/5" data-id="element-68" />
            </motion.div>

            <div className="flex flex-col items-center gap-3 md:gap-8 mb-5 md:mb-16" data-id="element-69">
              <CTAButton href="#pricing" className="uppercase tracking-widest px-6 py-4 md:px-16 md:py-7" data-id="element-70">
                QUIERO COMENZAR MI PROCESO
              </CTAButton>

              <div className="flex flex-wrap justify-center gap-3 md:gap-6 text-white/60 font-medium text-xs md:text-sm tracking-widest uppercase" data-id="element-71">
                <div className="flex items-center gap-2" data-id="element-72">
                  <FileText size={16} className="text-[#C9A84C]" data-id="element-73" /> 100% PDF
                </div>
                <div className="flex items-center gap-2" data-id="element-74">
                  <Zap size={16} className="text-[#C9A84C]" data-id="element-75" /> Acceso en 5 min
                </div>
                <div className="flex items-center gap-2" data-id="element-76">
                  <ShieldCheck size={16} className="text-[#C9A84C]" data-id="element-77" /> Garantía
                  7 días
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="lazy-section py-6 md:py-24 bg-[#1A0B2E] relative overflow-hidden" data-id="element-78">
        <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-79">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-81">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 max-w-5xl mx-auto text-center" data-id="element-82">
            {[{
            label: 'personas en proceso de cierre',
            value: '+1.200'
          }, {
            label: 'tomos y 3 bonos',
            value: '4'
          }, {
            label: 'páginas listas para trabajar',
            value: '270+'
          }, {
            label: 'calificación promedio',
            value: '4.9/5',
            stars: true
          }].map((stat, i) => <div key={i} className="flex flex-col items-center" data-id="element-83">
                <p className="text-4xl md:text-6xl font-bold text-[#C9A84C] mb-3" data-id="element-84">
                  {stat.value}
                </p>
                {stat.stars && <div className="flex justify-center text-[#C9A84C] mb-3" data-id="element-85">
                    {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-86" />)}
                  </div>}
                <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/50 font-light max-w-[120px] leading-relaxed" data-id="element-87">
                  {stat.label}
                </p>
                {i < 3 && <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-12 bg-[#C9A84C]/20" data-id="element-88"></div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials 1 */}
      <section className="lazy-section py-7 md:py-32 bg-[#0a0412]" data-id="element-89">
        <div className="container mx-auto px-4" data-id="element-90">
          <div className="grid md:grid-cols-3 gap-5 md:gap-8 max-w-6xl mx-auto" data-id="element-91">
            {[{
            n: 'María V.',
            l: 'Buenos Aires · Lic. en Psicología',
            q: 'Llevaba tiempo comprendiendo por qué me costaba soltar esa relación. Con el Tomo II trabajé el Rito del Hilo Rojo y sentí que algo en mí comenzó a moverse.',
            i: 'MV'
          }, {
            n: 'Carolina A.',
            l: 'Córdoba · Maestra de meditación',
            q: 'El Tomo III me movió mucho por dentro. La carta al niño de 7 años fue uno de los ejercicios más significativos que hice en mi propio camino.',
            i: 'CA'
          }, {
            n: 'Javier P.',
            l: 'Rosario · Psicólogo clínico',
            q: 'Pensé que era más de lo mismo y me equivoqué. La diferencia es que aquí haces algo, no solo lees. Y eso, para mí, cambió la forma de transitar el proceso.',
            i: 'JP'
          }].map((test, i) => <div key={i} className="bg-[#1A0B2E] p-6 md:p-10 rounded-3xl border border-[#C9A84C]/10 shadow-xl relative group hover:border-[#C9A84C]/30 transition-all duration-500" data-id="element-92">
                <div className="flex gap-1 mb-4 md:mb-6 text-[#C9A84C]" data-id="element-93">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-94" />)}
                </div>
                <p className="text-[#F5F0E8] italic mb-5 md:mb-8 leading-snug md:leading-relaxed text-base md:text-lg font-light" data-id="element-95">
                  "{test.q}"
                </p>
                <div className="flex items-center gap-4" data-id="element-96">
                  <div className="w-12 h-12 bg-[#8900F2]/30 rounded-full flex items-center justify-center font-bold text-[#8900F2] border border-[#8900F2]/50" data-id="element-97">
                    {test.i}
                  </div>
                  <div data-id="element-98">
                    <p className="font-bold text-[#C9A84C] tracking-wide" data-id="element-99">
                      {test.n}
                    </p>
                    <p className="text-xs text-white/40 uppercase tracking-widest" data-id="element-100">
                      {test.l}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="lazy-section py-7 md:py-32 bg-[#1A0B2E]" data-id="element-101">
        <div className="container mx-auto px-4" data-id="element-102">
          <div className="max-w-4xl mx-auto text-center" data-id="element-103">
            <h2 className="text-3xl md:text-6xl font-bold mb-6 md:mb-12 text-[#C9A84C] leading-tight" data-id="element-104">
              ¿Cuántas veces más vas a comprender el problema sin poder
              soltarlo?
            </h2>

            <div className="space-y-4 md:space-y-8 text-base md:text-xl font-light leading-[1.5] md:leading-[1.6] text-white/80" data-id="element-105">
              <p data-id="element-106">
                Tal vez ya hiciste terapia. Ya leíste los libros. Ya analizaste
                cada detalle de lo que pasó.
              </p>
              <p data-id="element-107">
                Y sin embargo ahí está. Ese pensamiento que vuelve a las 3 de la
                mañana. Esa persona que ya no está pero ocupa más espacio en tu
                cabeza que las que sí están. Esa versión de ti que quedó
                atrapada en algo que ya terminó.
              </p>
              <p data-id="element-108">
                No es debilidad. No es falta de esfuerzo. Es que pocas veces nos
                enseñan a dialogar con la parte que sostiene el vínculo:{' '}
                <span className="text-white font-bold" data-id="element-109">tu inconsciente.</span>
              </p>
              <p data-id="element-110">
                Y el inconsciente no siempre responde a las explicaciones.{' '}
                <span className="text-[#8900F2] font-bold" data-id="element-111">
                  Resuena con gestos. Con símbolos. Con rituales hechos con
                  intención.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms List */}
      <section className="lazy-section py-7 md:py-32 bg-gradient-to-b from-[#1A0B2E] to-[#2D0050]/30" data-id="element-112">
        <div className="container mx-auto px-4" data-id="element-113">
          <SectionTitle subtitle="Si te sientes identificado/a con alguno de estos puntos, este material fue pensado para ti." data-id="element-114">
            Quizás sigues cargando algo que ya deberías haber soltado si...
          </SectionTitle>

          <div className="max-w-4xl mx-auto grid gap-2 md:gap-4" data-id="element-115">
            {['Comprendes racionalmente el problema pero no puedes dejar de pensar en él', 'Bloqueaste a esa persona pero igual la buscas o piensas en ella', 'Comienzas bien el día y de la nada algo te dispara el dolor de nuevo', 'Ya fuiste a terapia y aún así el nudo en el estómago sigue ahí', 'Sientes que cargas culpas o mandatos que no elegiste pero no sabes cómo soltar', 'Sientes que algo tuyo sabotea las cosas justo cuando estás bien', 'Necesitas un cierre que nunca llegó y no sabes cómo dártelo a ti mismo', 'Sientes que hay una versión de ti esperando ser liberada'].map((item, i) => <div key={i} className="group flex items-center gap-3 md:gap-6 p-3 md:p-6 border-b border-[#C9A84C]/10 hover:bg-white/5 transition-all" data-id="element-116">
                <Moon className="text-[#8900F2] shrink-0 group-hover:rotate-12 transition-transform" size={20} data-id="element-117" />
                <p className="text-base md:text-xl font-light leading-snug md:leading-normal text-white/90" data-id="element-118">
                  {item}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Break Question */}
      <section className="lazy-section relative py-8 md:py-48 overflow-hidden" data-id="element-119">
        <div className="absolute inset-0 z-0" data-id="element-120">
          <img src="https://images.unsplash.com/photo-1464802686167-b939a6910659?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover opacity-30" alt="Nebula Background" loading="lazy" decoding="async" data-id="element-121" />
          <div className="absolute inset-0 bg-[#1A0B2E]/60 backdrop-blur-[2px]" data-id="element-122"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center" data-id="element-123">
          <motion.h2 initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} className="text-3xl md:text-7xl font-bold text-[#C9A84C] leading-tight max-w-5xl mx-auto drop-shadow-[0_0_20px_rgba(137,0,242,0.5)]" data-id="element-124">
            ¿Y si esta fuera la última vez que atraviesas una de estas noches
            completamente sin herramientas?
          </motion.h2>
        </div>
      </section>

      {/* Product Presentation */}
      <section className="lazy-section py-7 md:py-32 bg-[#1A0B2E] relative overflow-hidden" data-id="element-125">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none" data-id="element-126">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#8900F2] blur-[200px] opacity-10 rounded-full" data-id="element-127"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10" data-id="element-128">
          <div className="inline-block bg-[#8900F2] text-white px-6 md:px-8 py-2 rounded-full font-bold text-xs md:text-sm uppercase tracking-[0.3em] mb-4 md:mb-8 shadow-[0_0_20px_rgba(137,0,242,0.5)]" data-id="element-129">
            SISTEMA COMPLETO DE TRANSFORMACIÓN
          </div>
          <h2 className="md:text-7xl font-bold mb-3 md:mb-6 text-white text-[24px] md:text-[28px] leading-tight" data-id="element-130">
            🌑 EL MÉTODO DE LOS 33 RITOS™ <br data-id="element-131" />
            <span className="text-[#C9A84C]" data-id="element-132">
              PACK COMPLETO EDICIONES UMBRAL
            </span>
          </h2>
          <p className="text-base md:text-3xl text-white/60 mb-8 md:mb-16 max-w-4xl mx-auto font-light leading-snug md:leading-relaxed" data-id="element-133">
            4 Tomos + 3 Bonos · 270+ páginas · Un material de Psicología
            Simbólica en español para acompañar tu proceso de cerrar, soltar y
            renacer.
          </p>

          <div className="max-w-5xl mx-auto mb-8 md:mb-20 relative group" data-id="element-134">
            <div className="absolute inset-0 bg-[#8900F2] blur-[120px] opacity-20 group-hover:opacity-30 transition-opacity" data-id="element-135"></div>
            <img src="https://cdn.magicpatterns.com/uploads/nHMCVwm4Fede3rMn2MjRDL/metodo_33_ritos_landing_hero_1024.webp" alt="Pack Completo Mockup" loading="lazy" decoding="async" width="1024" height="1024" className="relative z-10 w-full h-auto rounded-3xl shadow-2xl border border-white/10" data-id="element-136" />
          </div>

          <div className="grid md:grid-cols-3 gap-3 md:gap-6 max-w-5xl mx-auto" data-id="element-137">
            {['Inspirado en psicología profunda y trabajo simbólico', '100% adaptado a la realidad latinoamericana', 'Cada rito tiene un paso a paso claro y detallado'].map((badge, i) => <div key={i} className="bg-white/5 border border-[#C9A84C]/20 p-4 md:p-6 rounded-2xl backdrop-blur-md flex items-center justify-center text-xs md:text-sm font-bold uppercase tracking-widest text-[#C9A84C]" data-id="element-138">
                ✦ {badge}
              </div>)}
          </div>
        </div>
      </section>

      {/* The 4 Tomos */}
      <section className="lazy-section py-7 md:py-32 bg-[#0a0412]" data-id="element-139">
        <div className="container mx-auto px-4" data-id="element-140">
          <SectionTitle subtitle="Cada tomo trabaja una capa diferente de tu proceso de transformación." data-id="element-141">
            📚 4 TOMOS MAESTROS
          </SectionTitle>

          <div className="grid gap-5 md:gap-12 max-w-5xl mx-auto" data-id="element-142">
            {[{
            n: 'I',
            t: 'EL ORÁCULO DEL INCONSCIENTE',
            d: 'El primer paso: aprender el idioma que tu inconsciente entiende.',
            f: ['El Bucle Mental y por qué ocurre', 'La Ley del Gesto Psicomágico', 'Kit de herramientas sagradas', 'Protocolo de seguridad emocional', '5 Ritos de Iniciación completos']
          }, {
            n: 'II',
            t: 'CORTES VINCULARES',
            d: 'Para soltar lo que el corazón todavía sostiene.',
            f: ['La psicología del vínculo que no cierra', '10 Ritos de corte vincular', 'Ritos para ex-parejas, amigos y familia', 'El rito del perdón a uno mismo', 'La puerta que se cierra para siempre']
          }, {
            n: 'III',
            t: 'PURGA DE SOMBRAS',
            d: 'Para liberar lo que heredaste y lo que reprimiste.',
            f: ['La psicología de la Sombra (Jung)', '12 Ritos de purga profunda', 'Trabajo con el niño interior', 'Deuda del linaje y trauma familiar', 'El pacto con la sombra']
          }, {
            n: 'IV',
            t: 'ARQUITECTURA DEL RENACIMIENTO',
            d: 'Construir la nueva versión de ti con actos conscientes.',
            f: ['La neurociencia del renacimiento', '11 Ritos de poder personal', 'El mapa del yo futuro', 'La ceremonia del nombre nuevo', 'El Gran Rito de Cierre Final']
          }].map((tomo, i) => <div key={i} className="group relative bg-[#1A0B2E] rounded-[1.5rem] md:rounded-[2rem] border border-[#C9A84C]/10 overflow-hidden hover:border-[#C9A84C]/40 transition-all duration-500" data-id="element-143">
                <div className="absolute top-0 right-0 p-6 md:p-12 opacity-5 group-hover:opacity-10 transition-opacity" data-id="element-144">
                  <span className="text-[6rem] md:text-[12rem] font-bold text-[#C9A84C] leading-none" data-id="element-145">
                    {tomo.n}
                  </span>
                </div>
                <div className="p-5 md:p-16 relative z-10" data-id="element-146">
                  <span className="text-[#C9A84C] font-bold text-xs md:text-sm tracking-[0.4em] mb-2 md:mb-4 block" data-id="element-147">
                    TOMO {tomo.n}
                  </span>
                  <h3 className="text-2xl md:text-5xl font-bold mb-3 md:mb-6 text-white leading-tight" data-id="element-148">
                    {tomo.t}
                  </h3>
                  <p className="text-[#8900F2] font-bold mb-3 md:mb-8 text-xs md:text-sm uppercase tracking-widest" data-id="element-149">
                    Contenido Exclusivo
                  </p>
                  <p className="text-white/60 mb-5 md:mb-10 text-base md:text-xl font-light leading-snug md:leading-relaxed max-w-2xl" data-id="element-150">
                    {tomo.d}
                  </p>
                  <ul className="grid md:grid-cols-2 gap-y-2 md:gap-y-4 gap-x-12" data-id="element-151">
                    {tomo.f.map((feature, j) => <li key={j} className="flex items-center gap-3 md:gap-4 text-white/90 text-sm md:text-lg font-light" data-id="element-152">
                        <span className="text-[#8900F2]" data-id="element-153">✦</span> {feature}
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="lazy-section py-7 md:py-32 bg-[#1A0B2E]" data-id="element-154">
        <div className="container mx-auto px-4" data-id="element-155">
          <SectionTitle subtitle="3 bonos de regalo incluidos sin costo adicional con tu pack." data-id="element-156">
            🎁 3 BONOS ESTRATÉGICOS
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto" data-id="element-157">
            {[{
            n: '01',
            t: '33 CARTAS DE INTENCIÓN SAGRADA',
            d: 'Activa el campo simbólico correcto antes de cada rito. Una carta por cada uno de los 33 ritos.',
            v: 'Incluido'
          }, {
            n: '02',
            t: 'GUÍA DE CONTENCIÓN Y AUTORREGULACIÓN EMOCIONAL',
            d: 'Ritos de acompañamiento para los momentos difíciles y las noches que pesan más que el día.',
            v: 'Incluido'
          }, {
            n: '03',
            t: 'GUÍA DE MATERIALES Y ALTERNATIVAS ACCESIBLES',
            d: 'Todo lo que necesitas para los 33 ritos, dónde conseguirlo y alternativas accesibles para tu país.',
            v: 'Incluido'
          }].map((bono, i) => <div key={i} className="bg-[#0a0412] p-6 md:p-10 rounded-3xl border border-[#C9A84C]/10 relative overflow-hidden group hover:border-[#8900F2]/50 transition-all" data-id="element-158">
                <div className="absolute top-0 right-0 bg-[#8900F2] text-white text-[10px] font-bold px-4 md:px-6 py-1.5 md:py-2 rounded-bl-2xl uppercase tracking-widest" data-id="element-159">
                  Gratis
                </div>
                <span className="text-[#C9A84C] font-bold text-xs md:text-sm mb-2 md:mb-4 block tracking-[0.3em]" data-id="element-160">
                  BONO {bono.n}
                </span>
                <h4 className="text-xl md:text-2xl font-bold mb-3 md:mb-6 text-white leading-tight" data-id="element-161">
                  {bono.t}
                </h4>
                <p className="text-white/50 text-base md:text-lg font-light mb-5 md:mb-10 leading-snug md:leading-relaxed" data-id="element-162">
                  {bono.d}
                </p>
                <p className="text-sm text-[#C9A84C] font-bold uppercase tracking-widest" data-id="element-163">
                  {bono.v}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-6 md:py-32 bg-[#0a0412] flex flex-col justify-center" id="pricing" data-id="element-164">
        <div className="container mx-auto px-4" data-id="element-165">
          <div className="md:hidden" data-id="element-166">
            <h2 className="text-2xl font-bold text-white text-center mb-1.5 leading-tight" data-id="element-167">
              COMIENZA TU CIERRE CON EL MÉTODO DE LOS 33 RITOS
            </h2>
            <p className="text-white/60 text-xs text-center mb-4 leading-snug px-2" data-id="element-168">
              Recibe los 4 Tomos + 3 Bonos en formato PDF directamente en tu
              email y comienza hoy mismo.
            </p>
          </div>
          <div className="hidden md:block" data-id="element-169">
            <SectionTitle light subtitle="Recibe los 4 Tomos + 3 Bonos en formato PDF directamente en tu email y comienza hoy mismo." data-id="element-170">
              COMIENZA TU CIERRE CON EL MÉTODO DE LOS 33 RITOS
            </SectionTitle>
          </div>

          <div className="hidden md:flex justify-center mb-16" data-id="element-171">
            <div className="bg-[#1A0B2E] p-8 rounded-3xl border border-[#C9A84C]/30 shadow-[0_0_50px_rgba(201,168,76,0.1)]" data-id="element-172">
              <CountdownTimer data-id="element-173" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto" data-id="element-174">
            <div className="bg-[#1A0B2E] rounded-[1.5rem] md:rounded-[3rem] p-4 md:p-16 text-white shadow-2xl flex flex-col border-2 md:border-4 border-[#C9A84C] relative overflow-hidden" data-id="element-175">
              <div className="mb-3 md:mb-10 text-center" data-id="element-176">
                <h3 className="text-lg md:text-4xl font-bold text-white mb-0.5 md:mb-3" data-id="element-177">
                  🌑 EL MÉTODO DE LOS 33 RITOS™
                </h3>
                <p className="text-white/40 text-[9px] md:text-sm uppercase tracking-widest" data-id="element-178">
                  Pack Completo · 4 Tomos + 3 Bonos
                </p>
              </div>

              {/* Price — strongest visual element of the block */}
              <div className="mb-5 md:mb-10 text-center relative" data-id="element-179">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#C9A84C]/70 font-bold mb-1" data-id="element-180">
                  Precio especial de lanzamiento
                </p>
                <p className="text-white/30 line-through text-base md:text-2xl mb-0.5 md:mb-2 font-medium" data-id="element-181">
                  USD 30
                </p>
                <p className="text-5xl md:text-8xl font-bold text-[#C9A84C] drop-shadow-[0_0_30px_rgba(201,168,76,0.4)] leading-none" data-id="element-182">
                  USD 10
                </p>
                <p className="text-sm md:text-2xl font-medium text-[#C9A84C]/80 mt-1 md:mt-2" data-id="element-183">
                  Pago único
                </p>
              </div>

              {/* Transformation benefits */}
              <ul className="flex flex-col gap-2.5 md:gap-4 mb-5 md:mb-10" data-id="element-184">
                {['Material completo para cerrar ciclos y soltar vínculos.', '4 Tomos Maestros + 3 Bonos de Regalo (formato PDF).', 'Guía de Contención y Autorregulación Emocional.', 'Guía de Materiales y Alternativas Accesibles.', 'Acceso inmediato por email para siempre.'].map((item, i) => <li key={i} className="flex items-start gap-2.5 md:gap-3 text-white/90 text-sm md:text-lg font-light leading-snug" data-id="element-185">
                    <CheckCircle2 size={18} className="text-[#C9A84C] shrink-0 mt-0.5 drop-shadow-[0_0_8px_rgba(201,168,76,0.4)]" strokeWidth={2.5} data-id="element-186" />
                    <span data-id="element-187">{item}</span>
                  </li>)}
              </ul>

              <PaymentTrustBlock data-id="element-188" />

              <CTAButton className="w-full text-sm md:text-2xl py-3 md:py-8 mb-1" data-id="element-189">
                QUIERO ACCEDER POR USD 10
              </CTAButton>
              <CTAMicroCopy data-id="element-190" />

              {/* Digital delivery clarity */}

              <TrustBadgesRow data-id="element-191" />

              {/* Elegant guarantee seal */}
              <div className="mt-5 md:mt-8 flex items-center justify-center gap-3 md:gap-4 bg-[#0a0412]/60 border border-[#C9A84C]/30 rounded-xl md:rounded-2xl p-3 md:p-5" data-id="element-192">
                <div className="shrink-0 w-10 h-10 md:w-14 md:h-14 rounded-full border-2 border-[#C9A84C] flex items-center justify-center bg-[#1A0B2E] shadow-[0_0_20px_rgba(201,168,76,0.25)]" data-id="element-193">
                  <ShieldCheck size={20} className="text-[#C9A84C] md:!w-7 md:!h-7" data-id="element-194" />
                </div>
                <div className="text-left" data-id="element-195">
                  <p className="text-[#C9A84C] font-bold text-sm md:text-lg tracking-wide leading-tight" data-id="element-196">
                    7 Días de Garantía Total
                  </p>
                  <p className="text-white/50 text-[10px] md:text-xs leading-snug" data-id="element-197">
                    Devolución del 100% sin preguntas.
                  </p>
                </div>
              </div>

              {/* Launch urgency */}
              <div className="mt-4 md:mt-6 bg-[#C9A84C]/10 text-[#C9A84C] p-2.5 md:p-4 rounded-xl text-center font-bold text-[11px] md:text-base border border-[#C9A84C]/30 tracking-wide leading-snug" data-id="element-198">
                ⏳ OFERTA DE LANZAMIENTO: Valor promocional disponible por
                tiempo limitado.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="lazy-section py-7 md:py-32 bg-[#1A0B2E]" data-id="element-199">
        <div className="container mx-auto px-4" data-id="element-200">
          <SectionTitle subtitle="El próximo ciclo que no cierres puede acompañarte durante meses." data-id="element-201">
            ⚖️ ¿CUÁNTO PESA NO TENERLO?
          </SectionTitle>

          <div className="max-w-4xl mx-auto space-y-3 md:space-y-6" data-id="element-202">
            {[{
            s: 'Pensamiento que no para',
            no: 'Bucle mental sin salida',
            yes: 'Tomo I: Rito del Primer Silencio'
          }, {
            s: 'Ex-pareja que no puedes soltar',
            no: 'Bloqueo digital que no alcanza',
            yes: 'Tomo II: 10 ritos de corte vincular'
          }, {
            s: 'Culpa que no te pertenece',
            no: 'Cargarla indefinidamente',
            yes: 'Tomo III: Rito de devolución del linaje'
          }, {
            s: 'Crisis a las 3 de la mañana',
            no: 'Sin herramientas, sola/o',
            yes: 'Bono 2: Guía de contención lista'
          }, {
            s: 'No saber cómo empezar',
            no: 'Parálisis e improvisación',
            yes: 'Bono 1: Carta de intención lista'
          }].map((row, i) => <div key={i} className="overflow-hidden rounded-2xl md:rounded-3xl border border-[#C9A84C]/20 shadow-xl bg-[#2D0050]/20" data-id="element-203">
                <div className="p-4 md:p-6 bg-[#2D0050] border-b border-[#C9A84C]/10" data-id="element-204">
                  <h4 className="text-[#C9A84C] font-bold uppercase tracking-widest text-xs md:text-sm mb-1" data-id="element-205">
                    SITUACIÓN
                  </h4>
                  <p className="text-lg md:text-2xl font-bold text-white leading-tight" data-id="element-206">
                    {row.s}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10" data-id="element-207">
                  <div className="p-4 md:p-6 bg-red-500/5" data-id="element-208">
                    <span className="text-red-400 font-bold text-xs uppercase tracking-widest block mb-1 md:mb-2" data-id="element-209">
                      ❌ SIN EL MÉTODO
                    </span>
                    <p className="text-red-400/80 italic text-base md:text-lg" data-id="element-210">
                      {row.no}
                    </p>
                  </div>
                  <div className="p-4 md:p-6 bg-[#00C48C]/5" data-id="element-211">
                    <span className="text-[#00C48C] font-bold text-xs uppercase tracking-widest block mb-1 md:mb-2" data-id="element-212">
                      ✅ CON EL MÉTODO
                    </span>
                    <p className="text-[#00C48C] font-bold text-base md:text-lg" data-id="element-213">
                      {row.yes}
                    </p>
                  </div>
                </div>
              </div>)}

            <div className="bg-[#2D0050] text-white p-5 md:p-10 text-center rounded-2xl md:rounded-3xl border border-[#C9A84C]/20 shadow-2xl mt-5 md:mt-8" data-id="element-214">
              <p className="text-base md:text-2xl font-bold tracking-wide leading-snug" data-id="element-215">
                "Tu proceso de cierre puede empezar hoy mismo." 🌑
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials 2 */}
      <section className="lazy-section py-7 md:py-32 bg-[#0a0412]" data-id="element-216">
        <div className="container mx-auto px-4" data-id="element-217">
          <SectionTitle subtitle="Más personas que transitaron su proceso con este material. Las experiencias son personales y los resultados pueden variar." data-id="element-218">
            ❤️ TESTIMONIOS
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-5xl mx-auto" data-id="element-219">
            {[{
            n: 'Sofía M.',
            l: 'Mendoza · Coach ontológica',
            q: 'Hice el Tomo II un domingo a la tarde con velas y todo. Lloré, me reí y al final sentí algo que no sentía hace años: la liviandad.',
            i: 'SM'
          }, {
            n: 'Ricardo P.',
            l: 'Buenos Aires · Terapeuta floral',
            q: 'La Guía de Contención la usé la primera semana, en una madrugada difícil. Me dio algo concreto para sostenerme en ese momento.',
            i: 'RP'
          }, {
            n: 'Andrea L.',
            l: 'Santa Fe · Docente universitaria',
            q: 'El Rito del Pacto con la Sombra fue el más difícil y el más liberador de todo el método. Sentí que me perdoné algo que cargaba hace 20 años.',
            i: 'AL'
          }, {
            n: 'Martín G.',
            l: 'Córdoba · Psicólogo clínico',
            q: 'Lo sugiero como complemento del proceso personal de quienes acompaño. La claridad del paso a paso me dio confianza para recomendarlo.',
            i: 'MG'
          }].map((test, i) => <div key={i} className="bg-[#1A0B2E] p-5 md:p-10 rounded-2xl md:rounded-3xl border border-[#C9A84C]/20 flex gap-4 md:gap-8 items-start group hover:border-[#8900F2]/50 transition-all" data-id="element-220">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#8900F2] rounded-full shrink-0 flex items-center justify-center font-bold text-white text-base md:text-xl shadow-[0_0_20px_rgba(137,0,242,0.4)]" data-id="element-221">
                  {test.i}
                </div>
                <div data-id="element-222">
                  <div className="flex gap-1 mb-2 md:mb-4 text-[#C9A84C]" data-id="element-223">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-224" />)}
                  </div>
                  <p className="text-white/80 italic mb-3 md:mb-6 text-base md:text-lg font-light leading-snug md:leading-relaxed" data-id="element-225">
                    "{test.q}"
                  </p>
                  <p className="font-bold text-[#C9A84C] tracking-wide text-sm md:text-base" data-id="element-226">
                    {test.n}
                  </p>
                  <p className="text-[10px] md:text-xs text-[#8900F2] uppercase tracking-[0.2em] font-bold mt-1" data-id="element-227">
                    {test.l}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="lazy-section py-7 md:py-32 bg-[#1A0B2E]" data-id="element-228">
        <div className="container mx-auto px-4 text-center" data-id="element-229">
          <div className="max-w-4xl mx-auto bg-[#0a0412] p-6 md:p-24 rounded-[1.5rem] md:rounded-[3rem] border-2 border-[#C9A84C] shadow-2xl relative overflow-hidden" data-id="element-230">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-[#1A0B2E] px-6 md:px-10 py-1.5 md:py-2 rounded-b-2xl font-bold text-[10px] md:text-xs tracking-widest uppercase" data-id="element-231">
              Garantía
            </div>
            <ShieldCheck size={56} className="text-[#C9A84C] mx-auto mb-4 md:mb-10 md:!w-20 md:!h-20 drop-shadow-[0_0_20px_rgba(201,168,76,0.4)]" data-id="element-232" />
            <h2 className="text-3xl md:text-6xl font-bold mb-3 md:mb-8 text-white leading-tight" data-id="element-233">
              GARANTÍA DE 7 DÍAS
            </h2>
            <p className="text-base md:text-2xl text-white/70 mb-4 md:mb-12 leading-[1.5] md:leading-relaxed font-light" data-id="element-234">
              Explora el material con tranquilidad.
              <span className="text-base md:text-xl text-white/60 block mt-4 md:mt-6 max-w-2xl mx-auto leading-[1.5]" data-id="element-235">
                Si durante los primeros 7 días consideras que el contenido no
                corresponde con la descripción presentada en esta página, podrás
                solicitar la devolución de acuerdo con las condiciones de
                garantía.
              </span>
            </p>
            <div className="flex flex-col items-center gap-1 md:gap-2" data-id="element-236">
              <p className="text-[10px] md:text-xs text-[#C9A84C] uppercase tracking-[0.4em] font-bold" data-id="element-237">
                Devolución según las condiciones de garantía
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="lazy-section py-7 md:py-32 bg-[#0a0412]" data-id="element-238">
        <div className="container mx-auto px-4" data-id="element-239">
          <SectionTitle subtitle="Despeja tus dudas y comienza hoy mismo." data-id="element-240">
            ❓ LO QUE MÁS NOS PREGUNTAN
          </SectionTitle>
          <div className="max-w-4xl mx-auto space-y-3 md:space-y-6" data-id="element-241">
            {[{
            q: '¿Para quién es este material?',
            a: 'Para cualquier persona que esté atravesando o haya atravesado una ruptura, pérdida o momento de quiebre y sienta que le cuesta soltar aunque lo comprenda. Es un material educativo y de desarrollo personal.'
          }, {
            q: '¿Necesito creer en lo espiritual para aprovecharlo?',
            a: 'No. El material está pensado como una propuesta reflexiva y simbólica. Puedes recorrerlo desde una mirada práctica, sin necesidad de creencias previas.'
          }, {
            q: '¿Cómo recibo el material?',
            a: 'Después de completar el pago recibes un email con los enlaces de descarga de los archivos PDF en el correo ingresado durante la compra.'
          }, {
            q: '¿Tiene garantía?',
            a: 'Sí. Cuentas con 7 días para explorar el material. Si consideras que el contenido no corresponde con lo descrito en esta página, puedes solicitar la devolución según las condiciones de garantía.'
          }, {
            q: '¿Es lo mismo que un libro de autoayuda?',
            a: 'La propuesta es que no solo leas, sino que también realices ejercicios. Cada rito incluye materiales, un paso a paso y una carta de integración para acompañar el proceso de forma activa.'
          }, {
            q: '¿Puedo usarlo mientras hago terapia?',
            a: 'Sí, puede acompañar tu proceso personal. Este material no sustituye la atención de profesionales de la salud mental; si estás en terapia, puedes comentarlo con tu profesional de confianza.'
          }, {
            q: 'Aviso importante',
            a: 'El Método de los 33 Ritos™ es un material educativo, reflexivo y de desarrollo personal. No constituye psicoterapia, diagnóstico, tratamiento médico ni atención de emergencia. No sustituye el acompañamiento de profesionales de la salud mental.'
          }].map((faq, i) => <details key={i} className="group bg-[#1A0B2E] rounded-2xl md:rounded-3xl overflow-hidden border border-white/5 hover:border-[#C9A84C]/20 transition-all" data-id="element-242">
                <summary className="flex items-center justify-between p-5 md:p-8 cursor-pointer font-bold text-base md:text-xl text-[#C9A84C] list-none leading-snug" data-id="element-243">
                  {faq.q}
                  <ChevronDown size={20} className="text-[#8900F2] group-open:rotate-180 transition-transform shrink-0 ml-3" data-id="element-244" />
                </summary>
                <div className="px-5 md:px-8 pb-5 md:pb-8 bg-[#1A0B2E] text-white/70 leading-snug md:leading-relaxed text-base md:text-lg font-light border-l-8 border-[#C9A84C]" data-id="element-245">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="lazy-section relative py-8 md:py-48 overflow-hidden text-center" data-id="element-246">
        <div className="absolute inset-0 z-0" data-id="element-247">
          <img src="https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover opacity-40" alt="Deep Space Background" loading="lazy" decoding="async" data-id="element-248" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A0B2E] via-[#1A0B2E]/80 to-transparent" data-id="element-249"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-250">
          <SectionTitle light subtitle="Tu proceso de cierre puede comenzar hoy mismo. 🌑" data-id="element-251">
            ⏰ EL PRECIO DE LANZAMIENTO CIERRA EN:
          </SectionTitle>

          <div className="flex justify-center mb-6 md:mb-16" data-id="element-252">
            <div className="bg-white/5 backdrop-blur-3xl p-5 md:p-10 rounded-2xl md:rounded-[2.5rem] border border-white/10 shadow-[0_0_100px_rgba(137,0,242,0.2)]" data-id="element-253">
              <CountdownTimer data-id="element-254" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 md:mb-20" data-id="element-255">
            <div className="bg-[#1A0B2E] rounded-2xl md:rounded-[3.5rem] p-5 md:p-20 text-white shadow-2xl flex flex-col border-2 md:border-4 border-[#C9A84C] relative" data-id="element-256">
              <h3 className="text-2xl md:text-5xl font-bold mb-4 md:mb-8 leading-tight" data-id="element-257">
                🌑 EL MÉTODO DE LOS 33 RITOS™
              </h3>
              <p className="text-white/30 line-through text-xl md:text-3xl mb-1 md:mb-2" data-id="element-258">
                USD 30
              </p>
              <p className="text-5xl md:text-9xl font-bold text-[#C9A84C] mb-5 md:mb-8 drop-shadow-[0_0_40px_rgba(201,168,76,0.4)]" data-id="element-259">
                USD 10
              </p>

              <PaymentTrustBlock data-id="element-260" />

              <CTAButton className="w-full text-base md:text-3xl py-4 md:py-10 shadow-[0_0_50px_rgba(22,163,74,0.5)]" data-id="element-261">
                ACCEDER AHORA POR USD 10
              </CTAButton>
              <CTAMicroCopy data-id="element-262" />

              <TrustBadgesRow data-id="element-263" />
            </div>
          </div>

          <div className="mt-8 md:mt-16 bg-[#C9A84C]/15 text-[#C9A84C] p-3 md:p-4 rounded-xl md:rounded-2xl inline-block font-bold border border-[#C9A84C]/30 tracking-widest uppercase text-xs md:text-sm" data-id="element-264">
            ⏳ Oferta de lanzamiento por tiempo limitado.
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-7 md:py-24 bg-[#0a0412] text-white/40 border-t border-white/5" data-id="element-265">
        <div className="container mx-auto px-4 text-center" data-id="element-266">
          <p className="font-bold text-white text-xl md:text-3xl mb-3 md:mb-6 font-display tracking-widest" data-id="element-267">
            🌑 EL MÉTODO DE LOS 33 RITOS™
          </p>
          <p className="text-sm md:text-lg mb-6 md:mb-12 max-w-3xl mx-auto leading-snug md:leading-relaxed font-light" data-id="element-268">
            Un material de Psicología Simbólica en español para acompañar tu
            proceso de cerrar ciclos, soltar vínculos y renacer desde adentro.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-16 mb-8 md:mb-16 text-xs md:text-sm tracking-widest uppercase font-bold" data-id="element-269">
            <a href="mailto:contacto@edicionesumbral.com" className="text-[#C9A84C] hover:text-white transition-colors flex items-center justify-center gap-3" data-id="element-270">
              <MessageSquare size={18} data-id="element-271" /> contacto@edicionesumbral.com
            </a>
            <div className="flex items-center justify-center gap-3 text-[#8900F2]" data-id="element-272">
              <Star size={18} data-id="element-273" /> @edicionesumbral
            </div>
          </div>
          <div className="w-full h-px bg-white/5 mb-6 md:mb-12" data-id="element-274"></div>
          <p className="text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 md:mb-6" data-id="element-275">
            © Ediciones Umbral™ 2026 — Todos los derechos reservados.
          </p>
          <p className="text-[#C9A84C] text-base md:text-2xl font-bold italic leading-snug" data-id="element-276">
            "Lo que se cierra con conciencia, abre espacio a lo nuevo." 🌑
          </p>
        </div>
      </footer>
    </div>;
}